import * as Configuration from './configuration'
import * as Data from './data'
import * as Forms from '@rushplay/forms'
import * as Hooks from './hooks'
import * as I18n from '@rushplay/i18n'
import * as Icons from './icons'
import * as Payments from './payments'
import * as R from 'ramda'
import * as React from 'react'
import * as ReactRedux from 'react-redux'
import * as ThemeUi from 'theme-ui'

import {PaymentMethod} from './payment-method-legendz'
import PropTypes from 'prop-types'
import Url from 'url'
import {toImageUrl} from './image'

const MAX_IMAGE_INDEX = 8

export function CoinStoreCard(props) {
  const translate = I18n.useTranslate()
  const imgproxyUrl = ReactRedux.useSelector(Configuration.getImgProxyUrl)
  const dpr = ReactRedux.useSelector(Configuration.getDevicePixelRatio)
  const amountCents = ReactRedux.useSelector(Configuration.getAmountCents)
  const amount = Hooks.useToAmount({
    amountCents: amountCents,
    minimumFractionDigits: '0',
  })

  const bgImage = toImageUrl(
    imgproxyUrl,
    Url.parse(translate('coin-store.card-bg') || '')?.pathname,
    {
      dpr,
      extension: 'webp',
      resizingHeight: '180',
      resizingWidth: '150',
    }
  )
  const coinImage = toImageUrl(
    imgproxyUrl,
    Url.parse(
      translate(`coin-store.image-${Math.min(props.index, MAX_IMAGE_INDEX)}`) ||
        ''
    )?.pathname,
    {
      dpr,
      extension: 'webp',
      resizingHeight: '230',
      resizingWidth: '300',
    }
  )

  const bigCoinsPileImage = toImageUrl(
    imgproxyUrl,
    Url.parse(translate('coin-store.coins-pile') || '')?.pathname,
    {
      dpr,
      extension: 'webp',
      resizingHeight: '140',
      resizingWidth: '327',
    }
  )

  const backgroundBeams = toImageUrl(
    imgproxyUrl,
    Url.parse(translate('coin-store.background-beams') || '')?.pathname,
    {
      dpr,
      extension: 'png',
      resizingHeight: '320',
      resizingWidth: '400',
    }
  )

  return (
    <ThemeUi.Box
      sx={{
        width: '100%',
        background: `url(${bigCoinsPileImage}), url(${backgroundBeams}), radial-gradient(36.57% 33.5% at 50.08% 50.06%, rgba(154, 68, 241, 0.2) 0%, rgba(154, 68, 241, 0.00) 100%)`,
        backgroundSize: '327px, 400px, 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center bottom, center 35%',
      }}
    >
      <ThemeUi.Box
        sx={{
          cursor: 'pointer',
          position: 'relative',
          width: '150px',
          height: '209px',
          margin: 'auto',
          border: 'none',
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'end',
        }}
      >
        {props.showBonus && (
          <ThemeUi.Box
            sx={{
              position: 'absolute',
              top: '0px',
              right: '0px',
              padding: '5px 10px',
              backgroundColor: '#D49C00',
              borderRadius: '4px',
              fontWeight: 800,
              fontSize: '11px',
              lineHeight: '9px',
              zIndex: 1,
            }}
          >
            {translate('coin-store.special')}
          </ThemeUi.Box>
        )}
        <ThemeUi.Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            aspectRatio: '150/180',
            position: 'relative',
            width: '100%',
            flexShrink: 0,
            background: `url(${bgImage})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'bottom center',
          }}
        >
          <ThemeUi.Box
            sx={{
              mt: '40%',
              fontSize: '17px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            <ThemeUi.Box>{props.amountGc}</ThemeUi.Box>
            <ThemeUi.Box>{translate('coin-store.gold-coins')}</ThemeUi.Box>
          </ThemeUi.Box>
          <ThemeUi.Box
            sx={{
              backgroundColor: '#7C24D5',
              py: '6px',
              px: '12px',
              width: '126px',
              m: 'auto',
              lineHeight: '21px',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            {amount}
          </ThemeUi.Box>
          {props.amountSc && (
            <ThemeUi.Box
              sx={{
                alignSelf: 'end',
                backgroundColor: '#4BA30D',
                py: '5px',
                fontSize: '12px',
                fontWeight: '900',
                px: '12px',
                width: '100%',
                borderBottomLeftRadius: '16px',
                borderBottomRightRadius: '16px',
                textAlign: 'center',
              }}
            >
              {translate('coin-store.bonus', {amount: props.amountSc})}
            </ThemeUi.Box>
          )}
          <ThemeUi.Box
            sx={{
              background: `url(${coinImage})`,
              position: 'absolute',
              top: '-20%',
              width: '150px',
              height: '109px',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          />
        </ThemeUi.Box>
      </ThemeUi.Box>
    </ThemeUi.Box>
  )
}

I18n.Loader.preload(
  [
    'coin-store.bonus',
    'coin-store.gold-coins',
    'coin-store.image-0',
    'coin-store.image-1',
    'coin-store.image-2',
    'coin-store.image-3',
    'coin-store.image-4',
    'coin-store.image-5',
    'coin-store.image-6',
    'coin-store.image-7',
    'coin-store.image-8',
    'coin-store.card-bg',
    'coin-store.special',
    'coin-store.coins-pile',
    'coin-store.background-beams',
  ],
  CoinStoreCard
)

CoinStoreCard.propTypes = {
  index: PropTypes.string.isRequired,
  amountSc: PropTypes.string,
  amountGc: PropTypes.string,
  showBonus: PropTypes.bool,
}

/**
 * Renders all the available payment-providers and their accounts, if any
 * @component PaymentMethodsLegendz
 * @returns {ReactNode}
 */
export function PaymentMethodsLegendz() {
  const providerField = Forms.useField('#/properties/provider')
  const accountIdField = Forms.useField('#/properties/accountId')
  const {transactionType} = Payments.useTransactionContext()

  const {amountSc, amountGc, showBonus, coinIndex} = ReactRedux.useSelector(
    Configuration.getSweepCasinoData
  )

  const getPaymentMethods = React.useCallback(
    (state) => Payments.getSortedPaymentMethods(state, transactionType),
    [transactionType]
  )

  const {paymentMethods, accounts} = ReactRedux.useSelector(getPaymentMethods)

  const translate = I18n.useTranslate(
    () => [`payment-methods.${transactionType}.disclaimer`],
    [transactionType]
  )
  const dispatch = ReactRedux.useDispatch()

  const handleChange = React.useCallback(
    (provider, accountId = '') => {
      accountIdField.onChangeValue(accountId)
      providerField.onChangeValue(provider)
      dispatch(Data.paymentMethodSelected(provider))
    },
    [accountIdField.onChangeValue, providerField.onChangeValue, dispatch]
  )

  React.useEffect(() => {
    if (paymentMethods.length === 1 && R.isEmpty(accounts)) {
      handleChange(paymentMethods[0].id)
    }
  }, [paymentMethods, handleChange])

  const [hideAccounts, setHideAccounts] = React.useState(true)

  const hasFee = React.useMemo(() => {
    const methodsHaveFee = paymentMethods.some((m) => m.feePercent > 0)
    const accountsHaveFee = accounts.some((a) => a.feePercent > 0)
    return methodsHaveFee || accountsHaveFee
  }, [paymentMethods, accounts])

  if (R.isEmpty(accounts) && R.isEmpty(paymentMethods)) {
    return (
      <ThemeUi.Grid
        sx={{
          pt: 0,
          placeItems: 'center',
          gridGap: 2,
        }}
      >
        <ThemeUi.Box
          sx={{
            color: 'muted',
            fontSize: '64px',
          }}
        >
          <Icons.Warning />
        </ThemeUi.Box>
        {translate('payment-methods.none-available')}
      </ThemeUi.Grid>
    )
  }

  return (
    <ThemeUi.Box sx={{maxWidth: '450px', mx: 'auto'}}>
      {transactionType === 'deposit' && (
        <>
          <CoinStoreCard
            index={coinIndex || 3}
            amountSc={amountSc}
            amountGc={amountGc}
            showBonus={showBonus || false}
          />
          <ThemeUi.Flex
            sx={{
              color: '#F5F5F6',
              justifyContent: 'center',
              pt: '24px',
              fontSize: '24px',
              fontWeight: 800,
              lineHeight: '28px',
            }}
          >
            {translate('payment-methods.choice')}
          </ThemeUi.Flex>
          <ThemeUi.Flex
            sx={{
              color: '#FDB022',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 800,
              lineHeight: '28px',
            }}
          >
            {translate('payment-methods.choice-amount', {amount: amountGc})}
          </ThemeUi.Flex>
        </>
      )}
      <ThemeUi.Flex
        sx={{
          color: '#BBC6E4',
          textAlign: 'center',
          m: 'auto',
          fontSize: '14px',
          lineHeight: '20px',
          pb: '24px',
          pt: 0,
        }}
      >
        {translate('payment-methods.statement')}
      </ThemeUi.Flex>
      {R.not(R.isEmpty(accounts)) && (
        <React.Fragment>
          <ThemeUi.Heading
            as="h6"
            sx={{
              fontSize: '16px',
              color: '#F5F5F6',
              fontWeight: 800,
              lineHeight: '26px',
            }}
          >
            {translate('payment-methods.with-accounts')}
          </ThemeUi.Heading>
          <ThemeUi.Flex sx={{flexDirection: 'column'}}>
            {R.map(
              (account) => (
                <ThemeUi.Box key={account.id} sx={{pt: 1}}>
                  <PaymentMethod
                    key={account.id}
                    feePercent={account.feePercent}
                    id={account.paymentMethodId}
                    lastSuccess={account.lastSuccess}
                    maskedAccount={account.maskedAccount}
                    maxCents={account.maxLimitCents}
                    minCents={account.minLimitCents}
                    name={`${account.type}-${account.id}`}
                    selected={accountIdField.value}
                    value={account.id}
                    type={R.toLower(account.type)}
                    onChange={(accountId) =>
                      handleChange(account.paymentMethodId, accountId)
                    }
                  />
                </ThemeUi.Box>
              ),
              // limit accounts to the most recent 2 and toggle to view more.
              hideAccounts ? accounts.slice(0, 2) : accounts
            )}
          </ThemeUi.Flex>
          {accounts?.length > 2 && (
            <ThemeUi.Box
              onClick={() => setHideAccounts((hide) => !hide)}
              variant="links.text"
              sx={{
                fontSize: 1,
                mb: 2,
              }}
            >
              {hideAccounts
                ? translate('payment-methods.show-all-accounts')
                : translate('payment-methods.show-less-accounts')}
            </ThemeUi.Box>
          )}
          {R.not(R.isEmpty(paymentMethods)) && (
            <ThemeUi.Heading
              as="h6"
              sx={{
                pt: '24px',
                fontSize: '16px',
                color: '#F5F5F6',
                fontWeight: 800,
                lineHeight: '26px',
              }}
            >
              {translate('payment-methods.without-accounts')}
            </ThemeUi.Heading>
          )}
        </React.Fragment>
      )}
      <ThemeUi.Flex sx={{flexDirection: 'column'}}>
        {paymentMethods.map((paymentMethod, index) => (
          <React.Fragment key={paymentMethod.id}>
            <PaymentMethod
              feePercent={paymentMethod.feePercent}
              id={paymentMethod.id}
              maxCents={paymentMethod.maxLimitCents}
              minCents={paymentMethod.minLimitCents}
              name={`${paymentMethod.type}-${paymentMethod.id}`}
              selected={providerField.value}
              type={R.toLower(paymentMethod.type)}
              value={paymentMethod.id}
              onChange={(provider) => handleChange(provider)}
            />
            {index < paymentMethods.length - 1 && (
              <ThemeUi.Box
                sx={{borderBottom: '1px solid', borderColor: '#242B3B'}}
              />
            )}
          </React.Fragment>
        ))}
      </ThemeUi.Flex>
      {hasFee ? (
        <ThemeUi.Flex
          sx={{
            pb: 1,
            pt: 0,
            fontSize: '8px',
          }}
        >
          {translate(`payment-methods.${transactionType}.disclaimer`)}
        </ThemeUi.Flex>
      ) : null}
    </ThemeUi.Box>
  )
}

I18n.Loader.preload(
  [
    'payment-methods.none-available',
    'payment-methods.with-accounts',
    'payment-methods.without-accounts',
    'payment-methods.show-all-accounts',
    'payment-methods.show-less-accounts',
    'payment-methods.statement',
    'payment-methods.choice',
    'payment-methods.choice-amount',
    'payment-methods.deposit-disclaimer',
  ],
  PaymentMethodsLegendz
)
